<template>
  <div v-if="rootItems.length > 0">
    <div>
      <MenuRootItem
        v-for="id in rootItems"
        :key="id"
        :item="items[id]"
        :class="{ active: active && !!expanded[id] }"
        :hideLabel="collapsed"
      />
    </div>
    <div v-if="active">
      <div v-for="(bounds, parentId) in expanded" :key="parentId">
        <div
          class="submenu"
          :style="submenuStyle(bounds, parentId)"
          @scroll="scrolling(parentId)"
          ref="latest"
        >
          <div
            v-if="showSubmenuLabel(parentId)"
            class="px-5 pt-3 pb-2 border-b font-bold"
            v-html="items[parentId].label"
          />
          <MenuChildrenItem
            v-for="childId in items[parentId].children"
            :key="childId"
            :item="items[childId]"
            :class="{
              active: !!expanded[childId],
              accentColour: !!expanded[childId] || highlighted === childId,
            }"
            @shortcutRemoved="onShortcutRemoved($event)"
          />
        </div>
        <div
          class="submenu-indicator"
          :class="{ active: activeSubmenu(parentId) }"
          :style="submenuStyle(bounds)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import events from '@wizer/core/events'
import MainMenuService from '@/services/MainMenuService'
import MenuRootItem from '@/components/menu/MenuRootItem'
import MenuChildrenItem from '@/components/menu/MenuChildrenItem'

export default {
  name: 'MainMenu',

  components: {
    MenuRootItem,
    MenuChildrenItem,
  },

  props: {
    collapsed: Boolean,
  },

  data() {
    return {
      active: false,
      rootItems: [],
      items: {},
      expanded: {},
      highlighted: null,
      mySettingsData: {},
    }
  },

  async mounted() {
    await this.populateMenu()
    this.subscribeToEvents()
    this.$emit('mainMenuLoaded')
  },

  methods: {
    subscribeToEvents() {
      events.subscribe('clicked', this.itemClicked)
      events.subscribe('over', this.itemOver)
      events.subscribe('out', this.itemOut)
    },

    async populateMenu() {
      const menuResponse = (await MainMenuService.getStructure()).data
      this.rootItems = []
      this.items = {}
      menuResponse.children
        .filter((child) => !!child)
        .filter((child) => !child.isMySettingsMenu)
        .forEach((child) => {
          const id = this._processMenuItem(child, [])
          this.rootItems.push(id)
        })

      const settingsIndex = _.findIndex(menuResponse.children, (item) => item.isMySettingsMenu)
      this.mySettingsData = menuResponse.children[settingsIndex]
    },

    _processMenuItem(itemData, path) {
      const id = itemData.id || _.uniqueId()
      let children
      if (itemData.children) {
        children = itemData.children
          .filter((child) => !!child)
          .map((child) => {
            const childId = this._processMenuItem(child, [...path, id])
            return childId
          })
      }
      this.items[id] = _.assign(itemData, { id, path, children })
      return id
    },

    itemOver(data) {
      this.highlighted = data.id
      this.activateSubmenu(data)
      this.$nextTick(() => {
        this.adjustMenuHeight(data)
      })
    },

    itemOut(data) {
      if (this.highlighted === data.id) {
        this.highlighted = null
      }
    },

    activateSubmenu(data) {
      const toExpand = {}
      data.path.forEach((id) => {
        toExpand[id] = this.expanded[id]
      })
      if (this.items[data.id]?.children) {
        let target = data.target
        while (target && !target.classList?.contains('menu-item')) {
          target = target.parentNode
        }
        if (target) {
          const bounds = target.getBoundingClientRect()
          toExpand[data.id] = {
            x: bounds.x + bounds.width,
            y: bounds.y - 1,
            height: bounds.bottom - bounds.y - 1,
          }
        }
      }
      this.expanded = toExpand
    },

    itemClicked(data) {
      if (!data.withAction) {
        this.showMenu()
      } else {
        this.hideMenu()
      }
    },

    showMenu() {
      this.active = true
      this.$nextTick(() => {
        this.adjustMenuHeight()
      })
    },

    adjustMenuHeight(data) {
      const currentSubMenu = _.last(this.$refs.latest)
      if (!currentSubMenu) {
        return
      }

      const documentHeight = document.body.getBoundingClientRect().height
      this.$nextTick(() => {
        if (currentSubMenu.clientHeight + currentSubMenu.offsetTop < documentHeight) {
          // do nothing, dropdown fits vertically
        } else if (
          documentHeight - (documentHeight - currentSubMenu.offsetTop) >
          currentSubMenu.clientHeight
        ) {
          // the list should dropup, as there is enough space to fit it at the top
          let parentHeight = 0
          if (data) {
            this.items[data.id].inverted = true
            parentHeight = data.ref.$el.clientHeight + 3
          }
          currentSubMenu.style.top = `${
            parseFloat(currentSubMenu.style.top) - currentSubMenu.clientHeight + parentHeight
          }px`
        } else {
          // otherwise, add scrollbar to the dropdown
          currentSubMenu.style.maxHeight = `${documentHeight - currentSubMenu.offsetTop}px`
        }
      })
    },

    hideMenu() {
      this.active = false
    },

    submenuStyle(bounds) {
      return {
        top: `${bounds.y}px`,
        left: `${bounds.x}px`,
      }
    },

    scrolling(id) {
      const keepExpanded = {}
      Object.keys(this.expanded).forEach((itemId) => {
        const itemPath = this.items[itemId].path
        if (itemPath.indexOf(id) < 0) {
          keepExpanded[itemId] = this.expanded[itemId]
        }
      })
      this.expanded = keepExpanded
    },

    activeSubmenu(parentId) {
      if (this.showSubmenuLabel(parentId)) {
        return false
      }
      const firstChild = this.getFirstChild(parentId)
      const firstChildHighlighted = this.highlighted === firstChild
      const firstChildExpanded = !!this.expanded[firstChild]
      return firstChildHighlighted || firstChildExpanded
    },

    getFirstChild(parentId) {
      const parent = this.items[parentId]
      if (parent.children) {
        if (parent.inverted) {
          return _.last(parent.children)
        }
        return _.first(parent.children)
      }
      return '-1'
    },

    showSubmenuLabel(parentId) {
      return this.collapsed && this.rootItems.indexOf(parentId) > -1
    },

    onShortcutRemoved(data) {
      this.updateShortcuts(data.shortcuts)
    },

    updateShortcuts(shortcuts) {
      // Clear existing shortcuts
      const shortcutItems = this.items.mainMenu_shortcuts.children
      this.items = _.omit(this.items, shortcutItems)
      this.items.mainMenu_shortcuts.children = []
      // Repopulate shortcuts with a new list
      JSON.parse(shortcuts).forEach((shortcut) => {
        const uniqueId = _.uniqueId()
        this.items[uniqueId] = shortcut
        this.items[uniqueId].path = ['mainMenu_shortcuts']
        this.items[uniqueId].children = undefined
        this.items[uniqueId].id = uniqueId
        this.items.mainMenu_shortcuts.children.push(uniqueId)
      })
    },
    postMySettingsData() {
      document.getElementById('e360Frame').contentWindow.postMessage(
        {
          command: 'mysettings-data-response',
          mySettingsData: this.mySettingsData,
        },
        '*',
      )
    },
  },
}
</script>

<style scoped lang="postcss">
.submenu {
  @apply absolute w-64 bg-white text-black border border-gray-300 overflow-y-auto;
}
.submenu-indicator {
  @apply absolute pointer-events-none;
}
.submenu-indicator:before {
  @apply absolute w-3 h-3 border border-t-0 border-r-0 bg-white z-10;
  content: '';
  transform: rotate(45deg);
  top: 1rem;
  left: -0.4rem;
}
.submenu-indicator.active:before {
  @apply bg-gray-200;
}
</style>
